import { Suspense, lazy, useMemo, useState } from 'react';
import { Outlet, Navigate, useParams, createRoutesFromElements } from 'react-router-dom';
import ActionPlanRoutes from '@ashn/action-plan-react/Routes';
import healthyDealsRoutes from '@ashn/healthy-deals-react/HealthyDeals';
import HealthyDealsSkeleton from '@ashn/healthy-deals-react/Skeletons';
import getLiveSessionChildRoutes from '@ashn/live-session-react/Routes';
import useNavRoutes from '../../data/queryHooks/useNavRoutes';
import PaddedContainer from './containers/PaddedContainer';
import PageSEO from './PageSEO';
import { Programs } from '../../scripts/miscConstants';
import useHomePage from '../../data/queryHooks/useHomePage';
import { GetHomePageQuery } from '../../scripts/graphqlQueries';
import useSessionInfo from '../../data/queryHooks/useSessionInfo';
import useClientData from '../../data/queryHooks/useClientData';
import useFeatureFlags from '../../data/queryHooks/useFeatureFlags';
import ProtectedRoute from './ProtectedRoute';
import getApiEnvironment from '../../utils/environmentHelpers/getAPIEnvironments';
import Layout from './Layout';
import ScrollToTop from './ScrollToTop';
import FlowGuard from './FlowGuard';

const PaymentPage = lazy(() => import('../memberSessions/PaymentPage'));

const RescheduleAppointmentPage = lazy(() =>
	import('../memberSessions/RescheduleAppointmentPage')
);

const ConfirmSOSPage = lazy(() => import('../memberSessions/ConfirmSOSPage'));

const WaitingRoomPage = lazy(() => import('../memberSessions/WaitingRoomPage'));

const AppointmentDetailsPage = lazy(() =>
	import('../memberSessions/AppointmentDetailsPage')
);

const NotificationPreferencesPage = lazy(() =>
	import('../notificationPreferences/NotificationPreferencesPage')
);

const PaymentHistoryPage = lazy(() => import('../billing/PaymentHistoryPage'));

const PaymentAndBilling = lazy(() =>
	import('../billing/PaymentAndBillingPage')
);

const ProgramDetailPage = lazy(() =>
	import('../programDetailPage/ProgramDetailPage')
);
const ProgramDetailPageAnthemPreLogin = lazy(() =>
	import('../programDetailPage/anthem/ProgramDetailPageAnthemPreLogin')
);

const ProgramDetailPageAnthemPostLogin = lazy(() =>
	import(
		'../dashboard/VirtualFitnessAndRecovery/anthem/ProgramDetailPageAnthemPostLogin'
	)
);

const GenericStorePage = lazy(() =>
	import('../genericStorePage/GenericStorePage')
);

const SecureMessaging = lazy(() =>
	import('../secureMessaging/SecureMessaging')
);

const TermsAgreement = lazy(() =>
	import('./TermsAgreementWrapper')
);

const BuyHealthyPage = lazy(() =>
	import('../buyHealthy/BuyHealthyPage')
);

const RedirectToVptot = () => {
	const { clientCode } = useParams();
	return <Navigate to={`/vptot/${clientCode}`} replace />;
};

const IntercomChat = lazy(() =>
	import('../intercomChat/IntercomChat')
);

const buyHealthyTitle = 'BuyHealthy Marketplace';
const buyHealthyDescription = 'Discounts on popular brands to support your health, wellness, and fitness journey.';

const useReactRoutes = () => {
	const { intercomChatEnabled } = useFeatureFlags();

	const {
		homePageRoute,
		topNavBarRoutes,
		secondNavBarRoutes,
		errorPages,
		footerMenu,
	} = useNavRoutes();

	const {
		ssId, memberPrograms, paymentHistoryBlob,
		phoneNumber, secureMessaging, hasBuyHealthyVendorDiscounts,
		hasEnableMessageCenterEmailNotification, flows,
	} = useSessionInfo();

	// We only want react-router tags for React pages internal to this app, not for Ember
	// pages or external links. The AshCare React pages will have a non-null component.
	const reactLinks = [
		...homePageRoute,
		...topNavBarRoutes,
		...secondNavBarRoutes,
		...errorPages,
		...footerMenu,
	].filter(link => link.component);

	const { data } = useHomePage(GetHomePageQuery);
	const { clientData } = useClientData();

	const [appFlows, setAppFlows] = useState(flows);

	const routes = useMemo(() => [
		{
			path: '/empowered-decisions/anthem',
			element: <Navigate to="/anthem" replace />,
		},
		{
			path: '/vptr',
			element: <Navigate to="/vptot" replace />,
		},
		{
			path: '/vptr/:clientCode',
			element: <RedirectToVptot />,
		},
		{
			element: (
				<>
					<ScrollToTop />
					{intercomChatEnabled && (
						<IntercomChat
							intercomAppId="cqwegikk"
							timeoutSeconds={600}
							promptBeforeIdleSeconds={30}
						/>
					)}
					<FlowGuard appFlows={appFlows}>
						<Outlet />
					</FlowGuard>
				</>
			),
			children: [
				{
					element: (
						<Layout showHeader showFooter />
					),
					children: [
						{
							path: '/anthem',
							element: <ProgramDetailPageAnthemPreLogin />,
						},
					],
				},
				{
					element: (
						<Layout showHeader showNav showFooter />
					),
					children: [
						...reactLinks.map(link => ({
							path: `/${link.href}`,
							element: link.component,
						})),
						// add routes for custom clientcode program detail pages
						// loop through custom client only
						...(clientData ?? []).flatMap(client =>
							(client.customProgramCardsCollection?.items ?? []).map(program => {
								const slug = program.overridePage?.slug || program.detailPage?.slug;
								const pageId = program.overridePage?.sys?.id || program.detailPage?.sys?.id;
								return slug ? { path: `${slug}/${client.clientCode}`, element: <ProgramDetailPage pageId={pageId} /> } : null;
							}).filter(Boolean)
						),
						// add routes for program detail pages
						...(data?.ashCareHomePageCollection?.items[0]?.programCardsCollection?.items ?? []).map(program =>
							program.detailPage.isRoutable && program.detailPage.slug ? {
								path: program.detailPage.slug,
								element: <ProgramDetailPage pageId={program.detailPage.sys.id} />,
							} : null
						).filter(Boolean),

						// Note: add protected route componnets under this condition

						// Terms Agreement route: Displayed upon login
						{
							path: 'termsAgreement',
							element: (
								<ProtectedRoute>
									<PageSEO
										pageTitle="Latest Terms & Conditions"
										description="Read and Agree ASHCare’s most up-to-date terms, conditions, and disclaimers."
									/>
									<PaddedContainer>
										<TermsAgreement appFlows={appFlows} setAppFlows={setAppFlows} />
									</PaddedContainer>
								</ProtectedRoute>
							)
						},
						{
							path: '/memberSessions/waitingRoom/:appointmentId',
							element: (
								<ProtectedRoute isAuthorized={memberPrograms.includes(Programs.VirtualPhysicalTherapyAndRehabilitation)}>
									<WaitingRoomPage ssId={ssId} phoneNumber={phoneNumber} />
								</ProtectedRoute>
							),
						},
						{
							path: '/memberSessions/payment/:appointmentId',
							element: (
								<ProtectedRoute isAuthorized={memberPrograms.includes(Programs.VirtualPhysicalTherapyAndRehabilitation)}>
									<PaymentPage ssId={ssId} phoneNumber={phoneNumber} />
								</ProtectedRoute>
							),
						},
						{
							path: '/memberSessions/payment/confirmsos/:appointmentId',
							element: (
								<ProtectedRoute isAuthorized={memberPrograms.includes(Programs.VirtualPhysicalTherapyAndRehabilitation)}>
									<ConfirmSOSPage ssId={ssId} />
								</ProtectedRoute>
							),
						},
						{
							path: '/memberSessions/reschedule/:appointmentId',
							element: (
								<ProtectedRoute isAuthorized={memberPrograms.includes(Programs.VirtualPhysicalTherapyAndRehabilitation)}>
									<RescheduleAppointmentPage ssId={ssId} phoneNumber={phoneNumber} />
								</ProtectedRoute>
							),
						},
						{
							path: '/memberSessions/appointment',
							element: (
								<ProtectedRoute isAuthorized={memberPrograms.includes(Programs.VirtualPhysicalTherapyAndRehabilitation)}>
									<AppointmentDetailsPage />
								</ProtectedRoute>
							),
						},
						{
							path: '/message-center',
							element: (
								<ProtectedRoute
									isAuthorized={
										memberPrograms.length > 0 && secureMessaging
									}
								>
									<Suspense>
										<SecureMessaging />
									</Suspense>
								</ProtectedRoute>
							),
						},
						{
							path: 'actionPlan/*', // If you change this path, update all "pathToActionPlanRoutes" props in this project to match
							element: (
								<PaddedContainer>
									<ProtectedRoute isAuthorized={memberPrograms.includes(Programs.VirtualPhysicalTherapyAndRehabilitation)}>
										<ActionPlanRoutes
											ssId={ssId}
											apiEnv={getApiEnvironment()}
											navigateToResourceLibrary={resourceId => {
												window.location.assign(`/resourcelibrary/workouts/${resourceId}`);
											}}
											pathToActionPlanRoutes="/actionPlan"
										/>
									</ProtectedRoute>
								</PaddedContainer>
							),
						},
						{
							path: 'notificationPreferences',
							element: (
								<ProtectedRoute isAuthorized={hasEnableMessageCenterEmailNotification}>
									<Suspense>
										<NotificationPreferencesPage />
									</Suspense>
								</ProtectedRoute>
							),
						},
						{
							path: 'payment/history/*',
							element: (
								<ProtectedRoute isAuthorized={memberPrograms.includes(Programs.VirtualPhysicalTherapyAndRehabilitation)}>
									<Suspense>
										<PaymentHistoryPage ssId={ssId} paymentHistoryBlob={paymentHistoryBlob} />
									</Suspense>
								</ProtectedRoute>
							),
						},
						{
							path: 'payment/billing/*',
							element: (
								<ProtectedRoute isAuthorized={memberPrograms.includes(Programs.VirtualPhysicalTherapyAndRehabilitation)}>
									<Suspense>
										<PaymentAndBilling ssId={ssId} paymentHistoryBlob={paymentHistoryBlob} />
									</Suspense>
								</ProtectedRoute>
							),
						},
						{
							path: '/',
							element: (
								<ProtectedRoute isAuthorized={memberPrograms.includes(Programs.VirtualFitnessAndRecovery)}>
									<ProgramDetailPageAnthemPostLogin />
								</ProtectedRoute>
							),
						},
						{
							path: 'generic-store',
							element: <GenericStorePage />,
						},
						{
							path: '/buyhealthy/*',
							element: (
								<ProtectedRoute isAuthorized={hasBuyHealthyVendorDiscounts}>
									<Suspense fallback={
										<HealthyDealsSkeleton pageTitle={buyHealthyTitle} description={buyHealthyDescription} />
									}>
										<BuyHealthyPage pageTitle={buyHealthyTitle} />
									</Suspense>
								</ProtectedRoute>
							),
							id: 'healthyDealsRoutesData',
							loader: () => ({
								apiEnvironment: getApiEnvironment(),
								ssId,
								componentRootPath: '/buyhealthy',
								updatePageTitleAndDescription: (title, description) => {
									document.title = `${title} | ASHCare`;
									const metaDescription = document.querySelector(
										'meta[name="description"]'
									);
									if (metaDescription) {
										metaDescription.setAttribute('content', description);
									}
								},
								setMainTitleTo: buyHealthyTitle,
								description: buyHealthyDescription,
								hasOwnProvider: false,
								redirectToLogin: () => {
									window.location.href = '/identity/login';
								},
							}),
							children: healthyDealsRoutes,
						},
					],
				},
				// Below route(s) will not have header, nav and footer
				{
					element: (
						<Layout />
					),
					children: [
						{
							element: (
								<ProtectedRoute>
									<>
										<PageSEO
											pageTitle="Live Synchronous Session"
											description="Live synchronous video session between member and provider"
										/>
										<Outlet />
									</>
								</ProtectedRoute>
							),
							children: createRoutesFromElements(getLiveSessionChildRoutes(getApiEnvironment(), ssId)),
						},
					],
				},
			],
		},
	], [ssId]); // Recalculate only when necessary (session change)

	return routes;
};

export default useReactRoutes;
